/**
 * SectionNavigator Component
 *
 * Left panel showing the list of code sections/paragraphs
 * with status indicators and progress tracking.
 */

import { useState, useMemo } from 'react'
import { Search, Check, Circle, Loader2, AlertTriangle, ChevronRight, ChevronDown, Filter, PanelLeftClose } from 'lucide-react'
import './SectionNavigator.css'

// Section type definitions with labels and colors
const SECTION_TYPES = [
  { value: 'all', label: 'All Types', color: null },
  { value: 'paragraph', label: 'Paragraphs', color: '#22d3ee' },
  { value: 'section', label: 'Sections', color: '#a855f7' },
  { value: 'working-storage', label: 'Working Storage', color: '#f59e0b' },
]

function SectionNavigator({
  sections = [],
  selectedSectionId,
  onSelectSection,
  onCollapse,
  progress = { completed: 0, total: 0 },
  workbook = null,
  onStatusChange = null
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [scopeFilter, setScopeFilter] = useState('all')
  const [contextMenu, setContextMenu] = useState(null)

  // Get type counts for display
  const typeCounts = useMemo(() => {
    const counts = { all: sections.length }
    sections.forEach(s => {
      counts[s.type] = (counts[s.type] || 0) + 1
    })
    return counts
  }, [sections])

  // Filter sections by search term and type
  const filteredSections = useMemo(() => {
    let filtered = sections

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(s => s.type === typeFilter)
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(s =>
        s.name.toLowerCase().includes(term) ||
        s.displayName.toLowerCase().includes(term) ||
        s.description?.toLowerCase().includes(term)
      )
    }

    // Filter by scope (in workbook or not)
    if (scopeFilter === 'in_workbook' && workbook) {
      filtered = filtered.filter(s =>
        workbook.some(item =>
          item.name === s.name ||
          item.name === s.displayName ||
          (item.type === 'paragraph' && s.type === 'paragraph' && item.name === s.name)
        )
      )
    } else if (scopeFilter === 'not_in_workbook' && workbook) {
      filtered = filtered.filter(s =>
        !workbook.some(item =>
          item.name === s.name ||
          item.name === s.displayName ||
          (item.type === 'paragraph' && s.type === 'paragraph' && item.name === s.name)
        )
      )
    }

    return filtered
  }, [sections, searchTerm, typeFilter, scopeFilter, workbook])

  // Get type info for a section
  const getTypeInfo = (type) => {
    return SECTION_TYPES.find(t => t.value === type) || { label: type, color: '#6b7280' }
  }

  // Calculate progress percentage
  const progressPercent = progress.total > 0
    ? Math.round((progress.completed / progress.total) * 100)
    : 0

  // Get status icon for a section
  const getStatusIcon = (status) => {
    switch (status) {
      case 'converted':
        return <Check size={18} className="status-icon converted" />
      case 'in-progress':
        return <Loader2 size={18} className="status-icon in-progress" />
      case 'warning':
        return <AlertTriangle size={18} className="status-icon warning" />
      case 'error':
        return <AlertTriangle size={18} className="status-icon error" />
      default:
        return <Circle size={18} className="status-icon pending" />
    }
  }

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'converted': return 'Converted'
      case 'in-progress': return 'In Progress'
      case 'warning': return 'Has Warnings'
      case 'error': return 'Failed'
      default: return 'Pending'
    }
  }

  // Handle context menu
  const handleContextMenu = (e, section) => {
    e.preventDefault()
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      section
    })
  }

  // Handle status change
  const handleStatusChange = (sectionId, newStatus) => {
    if (onStatusChange) {
      onStatusChange(sectionId, newStatus)
    }
    setContextMenu(null)
  }

  return (
    <div className="section-navigator">
      {/* Header */}
      <div className="navigator-header">
        <div className="header-left">
          <h3>Sections</h3>
          <span className="section-count">{sections.length}</span>
        </div>
        {onCollapse && (
          <button
            className="collapse-btn"
            onClick={onCollapse}
            title="Collapse Sections Panel"
          >
            <PanelLeftClose size={16} />
          </button>
        )}
      </div>

      {/* Type Filter Dropdown */}
      <div className="navigator-filter">
        <Filter size={14} />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="type-filter-select"
        >
          {SECTION_TYPES.map(type => (
            <option key={type.value} value={type.value}>
              {type.label} ({typeCounts[type.value] || 0})
            </option>
          ))}
        </select>
        <ChevronDown size={14} className="select-arrow" />
      </div>

      {/* Scope Filter Dropdown */}
      {workbook && workbook.length > 0 && (
        <div className="navigator-filter">
          <Filter size={14} />
          <select
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
            className="scope-filter-select"
          >
            <option value="all">All Sections ({sections.length})</option>
            <option value="in_workbook">In Workbook ({workbook.length})</option>
            <option value="not_in_workbook">Not in Workbook ({sections.length - workbook.length})</option>
          </select>
          <ChevronDown size={14} className="select-arrow" />
        </div>
      )}

      {/* Search */}
      <div className="navigator-search">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search sections..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Section List */}
      <div className="section-list">
        {filteredSections.map((section) => (
          <div
            key={section.id}
            className={`section-item ${selectedSectionId === section.id ? 'selected' : ''} ${section.status}`}
            onClick={() => onSelectSection(section.id)}
            onContextMenu={(e) => handleContextMenu(e, section)}
          >
            <div className="section-info">
              <div className="section-name">{section.displayName}</div>
              <div className="section-meta">
                <span
                  className="section-type-badge"
                  style={{ backgroundColor: getTypeInfo(section.type).color + '20', color: getTypeInfo(section.type).color }}
                >
                  {getTypeInfo(section.type).label.replace('s', '')}
                </span>
                <span className="section-lines">L{section.startLine}-{section.endLine}</span>
              </div>
            </div>
            {selectedSectionId === section.id && (
              <ChevronRight size={18} className="section-indicator" />
            )}
          </div>
        ))}

        {filteredSections.length === 0 && (
          <div className="no-sections">
            {searchTerm ? 'No matching sections' : 'No sections found'}
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="navigator-progress">
        <div className="progress-header">
          <span>Progress</span>
          <span className="progress-text">{progress.completed} / {progress.total}</span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="progress-percent">{progressPercent}% complete</div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <>
          <div
            className="context-menu-overlay"
            onClick={() => setContextMenu(null)}
            onContextMenu={(e) => e.preventDefault()}
          />
          <div
            className="section-context-menu"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            <div className="context-menu-header">
              {contextMenu.section.displayName}
            </div>
            <button
              className="context-menu-item"
              onClick={() => handleStatusChange(contextMenu.section.id, 'converted')}
              disabled={contextMenu.section.status === 'converted'}
            >
              <Check size={14} />
              Mark as Complete
            </button>
            <button
              className="context-menu-item"
              onClick={() => handleStatusChange(contextMenu.section.id, 'in-progress')}
              disabled={contextMenu.section.status === 'in-progress'}
            >
              <Loader2 size={14} />
              Mark as In Progress
            </button>
            <button
              className="context-menu-item"
              onClick={() => handleStatusChange(contextMenu.section.id, 'pending')}
              disabled={!contextMenu.section.status || contextMenu.section.status === 'pending'}
            >
              <Circle size={14} />
              Mark as Pending
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default SectionNavigator
